/**
 * Admin → PvP Audit ledger.
 *
 * Read-only view of every PvP challenge created via
 * `/api/palgame/pvp/challenge`. Two typeahead filters (Challenger /
 * Opponent), a status dropdown, and OR/AND matching — same patterns
 * as the EconomyTransfers ledger.
 *
 * Stat cards surface biggest wager and status counts so admins can
 * spot abnormal expired/declined ratios at a glance.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import { Swords, RefreshCw, X } from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

const STATUS_OPTIONS = [
  { id: "",          label: "All statuses" },
  { id: "pending",   label: "Pending" },
  { id: "accepted",  label: "Accepted" },
  { id: "complete",  label: "Complete" },
  { id: "declined",  label: "Declined" },
  { id: "cancelled", label: "Cancelled" },
  { id: "expired",   label: "Expired" },
];

const STATUS_CLS = {
  pending:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
  accepted:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  complete:  "bg-[rgba(0,229,255,0.10)] text-[var(--teal)] border-[var(--teal)]/30",
  declined:  "bg-white/5 text-[var(--muted)] border-white/10",
  cancelled: "bg-white/5 text-[var(--muted)] border-white/10",
  expired:   "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export function PvpLedger() {
  const toast = useToast();
  const [challengerId, setChallengerId] = useState("");
  const [opponentId, setOpponentId] = useState("");
  const [status, setStatus] = useState("");
  const [match, setMatch] = useState("or");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [biggest, setBiggest] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200", match });
      if (challengerId) params.set("challenger_id", challengerId);
      if (opponentId) params.set("opponent_id", opponentId);
      if (status) params.set("status", status);
      const { data } = await api.get(`/admin/pvp/log?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setBiggest(data.biggest_wager || 0);
      setStatusCounts(data.status_counts || {});
    } catch (e) {
      toast.error(apiError(e, "Couldn't load PvP audit log"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [challengerId, opponentId, status, match]);

  const clearAll = () => { setChallengerId(""); setOpponentId(""); setStatus(""); };

  const statusBadges = useMemo(
    () => Object.entries(statusCounts).sort(([, a], [, b]) => b - a),
    [statusCounts]
  );

  return (
    <div className="space-y-4 fade-up" data-testid="pvp-ledger-tab">
      <div className="glass-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Swords size={16} className="text-[var(--teal)]" />
            <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              PvP Audit Log
            </h3>
          </div>
          <div className="text-xs text-[var(--muted)]">
            {loading ? "Loading…" : `${items.length} shown · ${total.toLocaleString()} total challenges`}
          </div>
        </div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Every <code>/palgame/pvp/challenge</code> writes a row here with the wager held in escrow.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="glass-card p-4">
          <div className="label">Biggest Wager (filter)</div>
          <div className="text-2xl glow-gold mt-1" data-testid="pvp-biggest-wager">
            {biggest.toLocaleString()} <ChilletCoinIcon size={14}/>
          </div>
        </div>
        <div className="glass-card p-4 col-span-2 md:col-span-2">
          <div className="label">Status Breakdown (filter)</div>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="pvp-status-breakdown">
            {statusBadges.length === 0 && <span className="text-xs text-[var(--muted)]">—</span>}
            {statusBadges.map(([s, n]) => (
              <span key={s} className={`badge ${STATUS_CLS[s] || "border-white/10 text-[var(--muted)]"}`}>
                {s || "—"}: <strong className="ml-1">{n}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr_auto_auto_auto] gap-3 items-end">
        <Field label="Challenger">
          <UserSearchInput
            value={challengerId}
            onChange={setChallengerId}
            onPick={(row) => setChallengerId(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search…"
            dataTestid="pvp-challenger-search"
          />
        </Field>
        <Field label="Opponent">
          <UserSearchInput
            value={opponentId}
            onChange={setOpponentId}
            onPick={(row) => setOpponentId(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search…"
            dataTestid="pvp-opponent-search"
          />
        </Field>
        <Field label="Status">
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="pvp-status"
          >
            {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Match">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setMatch("or")}
              className={`btn btn-sm ${match === "or" ? "" : "btn-secondary"}`}
              data-testid="pvp-match-or"
            >
              OR
            </button>
            <button
              type="button"
              onClick={() => setMatch("and")}
              className={`btn btn-sm ${match === "and" ? "" : "btn-secondary"}`}
              data-testid="pvp-match-and"
            >
              AND
            </button>
          </div>
        </Field>
        <button
          onClick={clearAll}
          className="btn btn-sm btn-secondary"
          disabled={!challengerId && !opponentId && !status}
          data-testid="pvp-clear"
        >
          <X size={12} /> Clear
        </button>
      </div>

      <div className="glass-card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
          <span className="label !mb-0">Recent</span>
          <button onClick={load} className="btn btn-sm btn-secondary" disabled={loading} data-testid="pvp-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto max-h-[60dvh]">
          <table className="w-full min-w-[680px] text-sm" data-testid="pvp-table">
            <thead className="sticky top-0 bg-[#0c0510]/95 backdrop-blur-sm">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-4 py-2 w-36">When</th>
                <th className="px-4 py-2">Challenger</th>
                <th className="px-4 py-2">Opponent</th>
                <th className="px-4 py-2 text-right">Wager</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Winner</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`pvp-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs text-[var(--muted)] font-mono whitespace-nowrap">
                    {fmtTime(r.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setChallengerId(r.challenger_id)}
                      className="text-left hover:text-[var(--teal)] transition-colors"
                      title="Filter to this challenger"
                    >
                      <div className="text-xs truncate max-w-[180px]">{r.challenger_pal || "—"}</div>
                      <div className="text-[10px] font-mono text-[var(--muted)] truncate max-w-[180px]">{r.challenger_id}</div>
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setOpponentId(r.opponent_id)}
                      className="text-left hover:text-[var(--teal)] transition-colors"
                      title="Filter to this opponent"
                    >
                      <div className="text-xs truncate max-w-[180px]">{r.opponent_pal || "—"}</div>
                      <div className="text-[10px] font-mono text-[var(--muted)] truncate max-w-[180px]">{r.opponent_id}</div>
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right glow-gold whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">{r.wager.toLocaleString()} <ChilletCoinIcon size={12}/></span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge ${STATUS_CLS[r.status] || "border-white/10 text-[var(--muted)]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[10px] font-mono text-[var(--muted)] truncate max-w-[180px]">
                    {r.winner_id || "—"}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[var(--muted)] text-sm">
                    {(challengerId || opponentId || status)
                      ? "No challenges match the current filters."
                      : "No PvP challenges recorded yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "2-digit", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}
